import React from 'react';
import PropTypes from 'prop-types';

import i18n from '../../../i18n';

import Home from '../../../models/Home';
import User from '../../../models/User';

import Storage from '../../../utils/Storage';
import {
    Collapsible,
    Callout,
    List,
    Title,
    Text,
    Link,
    Container,
    Flex,
    Icon,
    Textarea
} from '@homeexchange/design';
import useGTM from '../../analytics/useGTM';
import Analytics from '../../../utils/analytics';
import { NAVIGATION_ACTION, NAVIGATION_AREAS, NAVIGATION_TEXT } from '../../analytics/analytics-constants';

export default class MessageField extends React.PureComponent {
    static propTypes = {
        user: PropTypes.instanceOf(User).isRequired,
        home: PropTypes.instanceOf(Home).isRequired,
        exchangeType: PropTypes.number.isRequired,
        conversationsStats: PropTypes.boolean,
        hasError: PropTypes.boolean,
        onChange: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.state = {
            messagePrewritten: false,
            currentMessage: this.loadLastMessage(props.user, props.home)
        };
        this.messageChange = this.messageChange.bind(this);
        this.getPreWrittenMessage = this.getPreWrittenMessage.bind(this);
        this.switchMessage = this.switchMessage.bind(this);
        this.getMessage = this.getMessage.bind(this);
        this.shouldDisplayMessageTemplate = this.shouldDisplayMessageTemplate.bind(this);
        this.onTrackNavigationButton = this.onTrackNavigationButton.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.exchangeType !== this.props.exchangeType && this.state.messagePrewritten) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                currentMessage: this.getPreWrittenMessage()
            });
        }
    }

    // load user's last sent message
    loadLastMessage(user, home) {
        let message = Storage.get(`${user.id}_last_contact_message`);
        if (!message) {
            return '';
        }
        const shouldBeReplaced = {
            userName: home.get('user').get('first_name'),
            userDestination: home.firstAdmin()
        };
        if (typeof message === 'string') {
            message = message.replace(new RegExp('{{userName}}|{{userDestination}}', 'g'), (matched) => {
                matched = matched.slice(2, matched.length - 2);
                return shouldBeReplaced[matched];
            });
        }
        return message;
    }

    messageChange(e) {
        const { onChange } = this.props;

        if (e) {
            const { value } = e.target;
            const message = value ? value : '';

            this.setState({ currentMessage: message });
            this.saveMessage(message);
            onChange(value);
        }
    }

    saveMessage(message) {
        const { user, home } = this.props;
        // shouldn't store the last character
        if (!message || message === '') {
            Storage.remove(`${user.id}_last_contact_message`);
            return;
        }
        // change name to a variable only if receiver's name is not the sender's name;
        // change also destination to a variable
        if (
            this.props.user.get('first_name') != home.get('user').get('first_name') &&
            typeof message === 'string'
        ) {
            message = message.replace(new RegExp(home.get('user').get('first_name'), 'g'), '{{userName}}');
            message = message.replace(new RegExp(home.firstAdmin(), 'g'), '{{userDestination}}');
        }

        Storage.set(`${this.props.user.id}_last_contact_message`, message);
    }

    getMessage() {
        return this.state.currentMessage;
    }

    getPreWrittenMessage() {
        const { user, home } = this.props;

        return i18n.t('exchange:page.exchangeRequest.message.input.template', {
            HostName: home.get('user').get('first_name'),
            GuestName: user.get('first_name')
        });
    }

    switchMessage() {
        const { onChange } = this.props;
        const { messagePrewritten } = this.state;
        let message = '';

        if (!messagePrewritten) {
            message = this.getPreWrittenMessage();
        }

        this.setState(
            {
                messagePrewritten: !messagePrewritten,
                currentMessage: message
            },
            () => {
                $('#content').trigger('change');
            }
        );

        this.onTrackNavigationButton(NAVIGATION_TEXT.USE_A_TEMPLATE_MESSAGE);
        onChange(message);
    }

    shouldDisplayMessageTemplate() {
        const { conversationsStats } = this.props;
        return conversationsStats['nb_conversations_created_by_user'] < 15;
    }

    onTrackNavigationButton(text) {
        const { fetchNavigation } = useGTM(this.props.user);
        Analytics.trackGTM(
            'button',
            fetchNavigation(NAVIGATION_ACTION.CLICK, NAVIGATION_AREAS.CONTACT_MODAL, text)
        );
    }

    render() {
        const { home, hasError } = this.props;

        return (
            <Flex className="message-field" direction="column" gap="1">
                <Flex alignItems="center" gap="3">
                    <Icon name="pencil" />
                    <Title color={hasError ? 'error' : 'default'} level={5}>
                        {i18n.t('exchange:page.exchangeRequest.message.input.label')}
                    </Title>
                </Flex>

                <Text
                    dangerouslySetInnerHTML={{
                        __html: i18n.t('exchange:page.exchangeRequest.message.input.helper', {
                            hostName: home.get('user').get('first_name')
                        })
                    }}
                />

                <Collapsible
                    className="message-field-collapsible"
                    labelClosed={i18n.t('exchange:page.exchangeRequest.tips.collapsible.title')}
                    labelOpen={i18n.t('exchange:page.exchangeRequest.tips.collapsible.title')}
                    openOn="bottom"
                    isHighlighted={true}
                    onChange={(isOpen) => {
                        if (isOpen) {
                            this.onTrackNavigationButton(NAVIGATION_TEXT.TIPS_FROM_OUR_COMMUNITY);
                        }
                    }}
                >
                    <List
                        elements={[
                            {
                                title: i18n.t('exchange:page.exchangeRequest.tips.collapsible.title1'),
                                text: i18n.t('exchange:page.exchangeRequest.tips.collapsible.body1')
                            },
                            {
                                title: i18n.t('exchange:page.exchangeRequest.tips.collapsible.title2'),
                                text: i18n.t('exchange:page.exchangeRequest.tips.collapsible.body2')
                            },
                            {
                                title: i18n.t('exchange:page.exchangeRequest.tips.collapsible.title3'),
                                text: i18n.t('exchange:page.exchangeRequest.tips.collapsible.body3')
                            }
                        ]}
                    />
                </Collapsible>

                <Container className="message-field-text-area-container">
                    <Textarea
                        status={hasError ? 'error' : null}
                        value={this.getMessage()}
                        onChange={this.messageChange}
                        onBlur={this.messageChange}
                        placeholder={i18n.t('exchange:page.exchangeRequest.message.input.placeholder')}
                        message={i18n.t('common:validity_valueMissing')}
                    />
                </Container>

                {this.shouldDisplayMessageTemplate() && (
                    <Callout>
                        <Title level={5}>
                            {i18n.t('exchange:page.exchangeRequest.message.input.callout.title')}
                        </Title>
                        <Text>{i18n.t('exchange:page.exchangeRequest.message.input.callout.body')}</Text>
                        <Link href="#" onClick={this.switchMessage}>
                            {i18n.t('exchange:page.exchangeRequest.message.input.callout.cta')}
                        </Link>
                    </Callout>
                )}
            </Flex>
        );
    }
}
